import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import * as PreQualActions from './prequal.actions';
import { PreQualService } from '../../services/prequal.service';

@Injectable()
export class PreQualEffects {
  private actions$ = inject(Actions);
  private prequalService = inject(PreQualService);

  private getErrorMessage(error: unknown): string {
    console.error('PreQual API Error:', error);
    if (error instanceof HttpErrorResponse) {
      console.error('Error details:', error.error);
      if (error.status === 0) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      if (error.status === 400) {
        if (typeof error.error === 'string') {
          return error.error;
        }
        if (error.error?.message) {
          return error.error.message;
        }
        if (error.error?.errors) {
          const errors = Object.values(error.error.errors).flat().join(' ');
          return errors || 'Please check your information and try again.';
        }
        return 'Please check your information and try again.';
      }
      if (error.status === 500) {
        if (error.error?.message) {
          return `Server error: ${error.error.message}`;
        }
        return 'Server error. Please try again later.';
      }
      return error.error?.message || error.message || 'An error occurred. Please try again.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  submitPreQualApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreQualActions.submitPreQualApplication),
      mergeMap(({ data }) =>
        this.prequalService.submitApplication(data).pipe(
          map(application => PreQualActions.submitPreQualApplicationSuccess({ application })),
          catchError(error =>
            of(PreQualActions.submitPreQualApplicationFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getPreQualApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreQualActions.getPreQualApplications),
      mergeMap(({ pageNumber = 1, pageSize = 10 }) =>
        this.prequalService.getApplications(pageNumber, pageSize).pipe(
          map(page => PreQualActions.getPreQualApplicationsSuccess({ page })),
          catchError(error =>
            of(PreQualActions.getPreQualApplicationsFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getPreQualApplicationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreQualActions.getPreQualApplicationById),
      mergeMap(({ id }) =>
        this.prequalService.getApplicationById(id).pipe(
          map(application => PreQualActions.getPreQualApplicationByIdSuccess({ application })),
          catchError(error =>
            of(PreQualActions.getPreQualApplicationByIdFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );
}